import { ProductCapabilities } from '@awork/_shared/services/product/product-capabilities.service'
import getHostname from '@awork/_shared/functions/get-hostname'
import { isCustomWorkspaceSubdomain } from '@awork/_shared/functions/workspace-subdomain'
import type { Subscription } from 'rxjs'
import {
  fromEvent,
  merge,
  Observable,
  ReplaySubject,
  startWith,
  debounceTime,
  distinctUntilChanged,
  refCount,
  publishReplay,
  share,
  filter,
  mapTo,
  map,
  shareReplay,
  throttleTime,
  tap,
  interval,
  of,
  switchMap,
  scan
} from 'rxjs'
import type { ElementRef, OnDestroy, Signal } from '@angular/core'
import { computed, Injectable, inject, NgZone, signal, assertInInjectionContext, untracked } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import type { Duration } from 'date-fns'

import { getPixelRatio } from '@awork/_shared/functions/browser-infos'
import {
  addDays,
  endOfWeek,
  isAfter,
  isWeekend,
  isWithinRange,
  durationToMilliseconds
} from '@awork/_shared/functions/date-fns-wrappers'
import { isFriday } from 'date-fns'
import { getBrowserInfo } from '@awork/_shared/functions/browser-info'
import { mobileRegex, tabletRegex } from '@awork/_shared/static-data/regular-expressions'
import isTest from '@awork/_shared/functions/is-test'
import { createResizeObserver, isResizeObserverAvailable } from '@awork/_shared/functions/resize-observable'
import { isIPv4Hostname, isLocalhostHostname, isRunnerHostname } from '@awork/_shared/functions/url-helpers'
import { parsePortlessHostname } from '@awork/_shared/functions/portless-hostname'
import InAppSpy from 'inapp-spy'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { DOCUMENT } from '@angular/common'

const { isInApp } = InAppSpy()

type IntercomFunction = (command: string, ...args: unknown[]) => void

export enum WINDOWSIZE {
  SMARTPHONE,
  TABLET,
  DESKTOP,
  LARGEDESKTOP,
  EXTRALARGEDESKTOP
}

/**
 * Lower bounds (px) for {@link WINDOWSIZE} bands used by {@link BrowserService.getDeviceCategoryBySize}.
 * Each category applies from its value up to the next breakpoint (exclusive).
 */
export const WINDOW_SIZE_MIN_WIDTH_PX: Record<Exclude<WINDOWSIZE, WINDOWSIZE.SMARTPHONE>, number> = {
  [WINDOWSIZE.TABLET]: 768,
  [WINDOWSIZE.DESKTOP]: 992,
  [WINDOWSIZE.LARGEDESKTOP]: 1200,
  [WINDOWSIZE.EXTRALARGEDESKTOP]: 1920
}

export enum DeviceCategory {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop'
}

const getWindowSize = () => ({
  height: window.innerHeight,
  width: window.innerWidth
})

const blackListedInAppBrowers = ['linkedin', 'instagram']

const createWindowSize$ = () =>
  fromEvent(window, 'resize').pipe(map(getWindowSize), startWith(getWindowSize()), publishReplay(1), refCount())

@Injectable({ providedIn: 'root' })
export class BrowserService implements OnDestroy {
  private readonly product = inject(ProductCapabilities)
  static instance: BrowserService
  private _win: Window
  private _hasTouch: boolean = undefined
  private _hasMouse: boolean = undefined
  private pixeFactor: number

  public lastDeviceCategory: WINDOWSIZE

  // browser window sizes
  width$: Observable<number>
  height$: Observable<number>

  private mainViewWidth$ = new ReplaySubject<number>(1)

  /**
   * Main-view width after layout panels take their space, with a 200 ms debounce.
   */
  readonly mainViewWidth = toSignal(this.selectMainViewWidth())

  // browser device size type
  deviceCategory$: Observable<WINDOWSIZE>
  private readonly _deviceSize = signal<WINDOWSIZE>(this.getDeviceCategoryBySize(window.innerWidth))
  readonly deviceCategory = computed(() => this._deviceSize())

  private windowSizeObserver: ResizeObserver

  // online state
  online$: Observable<boolean>

  // browser window resize
  resize$: Observable<{
    height: number
    width: number
  }>

  // browser or window visibility (focus) change event observable
  visibilityChange$: Observable<boolean> = new Observable<boolean>()

  private _contentSize$ = new ReplaySubject<{ width: number; height: number }>(1)
  private readonly contentSize = signal({ width: 0, height: 0 })

  private contentResizeObserver: ResizeObserver
  private mainContentResizeObserver: ResizeObserver
  private _isMainContentWidescreen$: Observable<boolean>

  readonly mainContentDeviceCategory = toSignal(this.mainContentDeviceCategory$)
  private _mainContentDeviceCategory$: Observable<WINDOWSIZE>

  // document click
  documentClick$: Observable<HTMLElement>
  documentPointerUp$: Observable<PointerEvent>

  private scrollingElement: Element
  private menuScrollingElement: Element
  private listScrollingElement: Element
  private detailScrollingElement: Element
  private popUpScrollingElement: Element
  private mainViewElementRef: ElementRef<HTMLElement>
  scrollingElement$ = new ReplaySubject<Element>(1)
  listScrollingElement$ = new ReplaySubject<Element>(1)
  detailScrollingElement$ = new ReplaySubject<Element>(1)

  // Emojis used for time tracking title
  titleRunningEmojis = ['🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛']
  titlePausedEmoji = '⏸️'

  // User Activity Tracking
  private readonly elapsedTimeSignal: Signal<number | null>
  private readonly contentResizeSubscription: Subscription
  private readonly gestureEndHandler = (evt: Event): void => {
    if ((evt as { scale?: number }).scale !== 1) {
      evt.preventDefault()
      document.body.style.transform = 'scale(1)'
    }
  }

  private readonly zone = inject(NgZone)
  private readonly logService = inject(LogService)
  private readonly document = inject(DOCUMENT)

  constructor() {
    BrowserService.instance = this
    this.observeDeviceSize()

    const windowSize$ = createWindowSize$()

    this.width$ = (windowSize$.pipe(map(size => size?.width)) as Observable<number>).pipe(
      distinctUntilChanged(),
      debounceTime(200),
      shareReplay(1)
    )

    this.height$ = (windowSize$.pipe(map(size => size?.height)) as Observable<number>).pipe(
      distinctUntilChanged(),
      debounceTime(200)
    )

    this.deviceCategory$ = this.width$.pipe(map(width => this.getDeviceCategoryBySize(width))) as Observable<WINDOWSIZE>

    this.online$ = merge(fromEvent(window, 'online').pipe(mapTo(true)), fromEvent(window, 'offline').pipe(mapTo(false)))

    // check if browser is a touch device by attaching an touch start
    // event handlet and removing it again
    const win = this.getWindow()

    if (win) {
      this.lastDeviceCategory = this.getDeviceCategoryBySize(win.innerWidth)

      this.zone.runOutsideAngular(() => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this

        if (this._hasTouch === undefined || this._hasMouse === undefined) {
          this._hasTouch = false
          this._hasMouse = true
          win.addEventListener('touchstart', function handleTouch() {
            that._hasTouch = true
            that._hasMouse = false

            win.removeEventListener('touchstart', handleTouch)
          })
        }
      })
    }

    this.disableZoomOnMobileDevices()

    this.resize$ = windowSize$

    // Creating an observable for click events
    const doc = this.getDocument()

    if (doc) {
      this.zone.runOutsideAngular(() => {
        this.documentClick$ = merge(
          fromEvent<MouseEvent>(doc, 'mousedown').pipe(map(event => event.target as HTMLElement)),
          fromEvent<MouseEvent>(doc, 'mouseup').pipe(map(event => event.target as HTMLElement)),
          fromEvent<TouchEvent>(doc, 'touchstart').pipe(map(event => event.touches[0].target as HTMLElement)),
          fromEvent<TouchEvent>(doc, 'click').pipe(map(event => event.target as HTMLElement))
        ).pipe(
          throttleTime(100),
          filter(el => el !== null),
          share()
        )

        this.documentPointerUp$ = fromEvent<PointerEvent>(doc, 'pointerup')
      })
    }

    // Creating an observable for visibility change events. Visible returns true.
    this.visibilityChange$ = merge(
      fromEvent(this.getDocument(), 'visibilitychange').pipe(map(() => !this.getDocument().hidden)),
      fromEvent(this.getWindow(), 'focus').pipe(map(() => true)),
      fromEvent(this.getWindow(), 'blur').pipe(map(() => false))
    )

    // Initialize the activity tracking timer after visibility change observable is initialized
    this.elapsedTimeSignal = toSignal(this.createActiveTimeStream(), {
      initialValue: null,
      manualCleanup: true
    })

    // subscribe here to the resize observable to ensure the signal "contentSize" is firing
    this.contentResizeSubscription = this.contentResize$.subscribe()
  }

  ngOnDestroy(): void {
    const win = this.getWindow()

    this.contentResizeSubscription.unsubscribe()
    this.contentResizeObserver?.disconnect()
    this.mainContentResizeObserver?.disconnect()
    this.windowSizeObserver?.disconnect()

    if (win) {
      win.removeEventListener('gestureend', this.gestureEndHandler)
    }

    if (BrowserService.instance === this) {
      Reflect.deleteProperty(BrowserService, 'instance')
    }
  }

  private setContentResizeObservable(): void {
    const scrollingElement = this.listScrollingElement || this.scrollingElement

    if (!scrollingElement || !window.ResizeObserver || isTest()) {
      return
    }

    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect()
    }

    this.contentResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.borderBoxSize?.length) {
          const { inlineSize: width, blockSize: height } = entry.borderBoxSize[0]

          this._contentSize$.next({ width, height })
        } else if (entry.contentRect) {
          const { width, height } = entry.contentRect

          this._contentSize$.next({ width, height })
        }
      })
    })

    this.contentResizeObserver.observe(scrollingElement)
  }

  get contentResize$(): Observable<{ width: number; height: number }> {
    return this._contentSize$.pipe(
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((prev, curr) => prev.width === curr.width && prev.height === curr.height),
      tap(size => this.contentSize.set(size))
    )
  }

  /**
   * Determines if the main content is widescreen
   * @returns {Observable<boolean>}
   */
  get isMainContentWidescreen$(): Observable<boolean> {
    if (this._isMainContentWidescreen$) {
      return this._isMainContentWidescreen$
    }

    return (this._isMainContentWidescreen$ = this.contentResize$.pipe(
      map(size => {
        return this.getDeviceCategoryBySize(size.width) === WINDOWSIZE.EXTRALARGEDESKTOP
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    ))
  }

  /**
   * Determines if the main content is widescreen
   * @returns {Signal<boolean>}
   */
  get isMainContentWidescreen(): Signal<boolean> {
    return computed(() => this.getIsMainContentWidescreen())
  }

  /**
   * Returns whether the main content size matches the extra large desktop category
   * @returns {boolean}
   * @withSignal {Signal<{ width: number; height: number }>} contentSize
   */
  private getIsMainContentWidescreen(): boolean {
    const size = this.contentSize()
    return this.getDeviceCategoryBySize(size.width) === WINDOWSIZE.EXTRALARGEDESKTOP
  }

  /**
   * Gets the main content size
   * This is the size of the container either the list in split view or the main scrolling element
   * @returns {Signal<{ width: number; height: number; }>}
   */
  get mainContentSize(): Signal<{ width: number; height: number }> {
    return this.contentSize.asReadonly()
  }

  /**
   * Gets the device category according to the main content size
   * @returns {Observable<WINDOWSIZE>}
   */
  get mainContentDeviceCategory$(): Observable<WINDOWSIZE> {
    if (this._mainContentDeviceCategory$) {
      return this._mainContentDeviceCategory$
    }

    return (this._mainContentDeviceCategory$ = this.contentResize$.pipe(
      map(size => {
        return this.getDeviceCategoryBySize(size.width)
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    ))
  }

  get isMobileScreen(): boolean {
    return this.lastDeviceCategory === WINDOWSIZE.SMARTPHONE
  }

  get isTabletScreen(): boolean {
    return this.lastDeviceCategory === WINDOWSIZE.TABLET
  }

  get isDesktopScreen(): boolean {
    return (
      this.lastDeviceCategory === WINDOWSIZE.DESKTOP ||
      this.lastDeviceCategory === WINDOWSIZE.LARGEDESKTOP ||
      this.lastDeviceCategory === WINDOWSIZE.EXTRALARGEDESKTOP
    )
  }

  fireResize(): void {
    const win = this.getWindow()

    if (win) {
      win.dispatchEvent(new Event('resize'))
    }
  }

  disableZoomOnMobileDevices(): void {
    const win = this.getWindow()

    if (win) {
      win.removeEventListener('gestureend', this.gestureEndHandler)
      win.addEventListener('gestureend', this.gestureEndHandler)
    }
  }

  getDeviceCategoryBySize(width: number): WINDOWSIZE {
    let category: WINDOWSIZE

    if (width < WINDOW_SIZE_MIN_WIDTH_PX[WINDOWSIZE.TABLET]) {
      category = WINDOWSIZE.SMARTPHONE
    } else if (width < WINDOW_SIZE_MIN_WIDTH_PX[WINDOWSIZE.DESKTOP]) {
      category = WINDOWSIZE.TABLET
    } else if (width < WINDOW_SIZE_MIN_WIDTH_PX[WINDOWSIZE.LARGEDESKTOP]) {
      category = WINDOWSIZE.DESKTOP
    } else if (width < WINDOW_SIZE_MIN_WIDTH_PX[WINDOWSIZE.EXTRALARGEDESKTOP]) {
      category = WINDOWSIZE.LARGEDESKTOP
    } else {
      category = WINDOWSIZE.EXTRALARGEDESKTOP
    }

    this.lastDeviceCategory = category

    return category
  }

  /**
   * Gets the device category based on the user agent
   * @returns {DeviceCategory}
   */
  getDeviceCategory(): DeviceCategory {
    if (mobileRegex.test(navigator.userAgent)) {
      return DeviceCategory.Mobile
    }

    if (tabletRegex.test(navigator.userAgent)) {
      return DeviceCategory.Tablet
    }

    return DeviceCategory.Desktop
  }

  /**
   * Is in-app browser. Some mobile/desktop applications open an in-app browser
   * that has some restrictions. Mostly related to authentication.
   * @returns {boolean}
   */
  isBlackListedInAppBrowser(): boolean {
    const userAgent = window.navigator.userAgent?.toLowerCase()

    return isInApp || blackListedInAppBrowers.some(app => userAgent?.includes(app))
  }

  /**
   * Enable or disable background scrolling. Good when opening modals etc.
   * @param {boolean} value Whether background scrolling should be enabled
   * @param {boolean} [restoreScrollPosition=true] Whether to restore scroll position
   */
  public setBodyScrolling(value: boolean, restoreScrollPosition = true) {
    this.zone.runOutsideAngular(() => {
      const doc = this.getDocument()

      if (doc && doc.body && doc.documentElement) {
        if (!value) {
          doc.body.style.top = -doc.documentElement.scrollTop + 'px'
          doc.body.classList.add('aw-lyt--overflow-hidden')
          doc.documentElement.classList.add('aw-lyt--overflow-hidden')
        } else {
          doc.body.classList.remove('aw-lyt--overflow-hidden')
          doc.documentElement.classList.remove('aw-lyt--overflow-hidden')

          if (restoreScrollPosition) {
            doc.documentElement.scrollTop = -Number(doc.body.style.top.replace('px', ''))
          }

          doc.body.style.top = 'initial'
        }
      }
    })
  }
  /**
   * Returns a boolean if scrolling is enabled or not
   * @returns {boolean}
   */
  public getBodyScrolling(): boolean {
    const doc = this.getDocument()

    if (doc) {
      return !doc.body.classList.contains('aw-lyt--overflow-hidden')
    }

    return true
  }

  /**
   * Returns the document object of the browser
   * @returns {Document}
   */
  public getDocument(): Document {
    return this.document
  }

  /**
   * Returns the document object of the browser
   * @returns {Window}
   */
  public getWindow(): Window {
    if (!this._win) {
      this._win = window
    }

    return this._win
  }

  /**
   * Checks whether the browser currently has a non-collapsed text selection.
   * @returns {boolean} True when text is selected
   */
  public hasTextSelection(): boolean {
    return this.getWindow().getSelection()?.isCollapsed === false
  }

  /**
   * Returns the Intercom client from the browser global scope.
   * @returns {IntercomFunction | undefined}
   */
  private getIntercom(): IntercomFunction | undefined {
    return this.getWindow().Intercom as IntercomFunction | undefined
  }

  /**
   * Returns true if the device is a touch device
   * @returns {boolean}
   */
  public isTouchDevice(): boolean {
    return this._hasTouch
  }

  /**
   * Returns true if the device is a touch device
   * @returns {boolean}
   */
  public isTouchOnlyDevice(): boolean {
    const hasTouch = 'ontouchend' in document
    return (this._hasTouch || hasTouch) && !this._hasMouse
  }

  /**
   * Returns true is device is IOs
   * @returns {boolean}
   */
  public isIOsDevice(): boolean {
    const isSafari = this.getBrowserName() === 'safari'
    const hasTouch = this.isTouchDevice() || 'ontouchend' in document

    return isSafari && hasTouch
  }

  public isIPadDevice(): boolean {
    return /iPad/.test(navigator.userAgent)
  }

  public isIPhone(): boolean {
    return /iPhone/.test(navigator.userAgent)
  }

  /**
   * Returns true if the device is a touch device
   * @returns {boolean}
   */
  public isMouseDevice(): boolean {
    return this._hasMouse
  }

  /**
   * Gets the current hostname's subdomain, if the hostname represents one.
   * @returns {string} The detected subdomain, or null for local/IP hosts and runner entry hosts
   */
  public getSubdomain(): string {
    const host = this.getWindow().location.hostname

    const portlessHostname = parsePortlessHostname(host)

    if (portlessHostname) {
      return portlessHostname.subdomain
    }

    if (isLocalhostHostname(host) || isIPv4Hostname(host)) {
      return null
    }

    if (isRunnerHostname(host) && host === getHostname(host)) {
      return null
    }

    const regex = /(.*?)\.(?=[^/]*\..{2,5})/i

    const result = host.match(regex)

    return result ? result[1] : null
  }

  /**
   * Determines if the actual subdomain is a custom one
   * @returns {boolean}
   */
  public isCustomSubdomain(): boolean {
    return isCustomWorkspaceSubdomain(this.getSubdomain())
  }

  /**
   * Checks the pixel radius of the screen the browser is running it and returns the number.
   * Used for images.
   * @returns {number}
   */
  public getPixelRatioFactor(): number {
    if (!this.pixeFactor) {
      const win = this.getWindow()

      this.pixeFactor = getPixelRatio(win)
    }

    return this.pixeFactor
  }

  public blurMainContent(blur: boolean, allFramework = true) {
    this.zone.runOutsideAngular(() => {
      const doc = this.getDocument()

      if (doc) {
        let lytEl = doc.getElementById('aw-lyt')

        if (!allFramework) {
          lytEl = doc.getElementById('aw-lyt__main')
        }

        if (lytEl) {
          if (blur) {
            lytEl.classList.add('blur')
          } else {
            lytEl.classList.remove('blur')
          }
        }
      }
    })
  }

  /**
   * Returns the timezone of the current browser
   * @returns {string}
   */
  getTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin'
    } catch {
      return 'Europe/Berlin'
    }
  }

  /**
   * Sets the title of the app
   * @param {string} title
   */
  setAppTitle(title: string): void {
    const doc = this.getDocument()

    if (doc) {
      if (title && title.trim() !== '') {
        doc.title = `${this.product.branding.displayName} • ${title}`.trim()
      } else {
        doc.title = this.product.branding.displayName
      }
    }
  }

  /**
   * Opens the help
   * @param {string} userId
   * @param {string} searchParam
   */
  openHelp(userId: string, searchParam?: string): void {
    let searchQueryParam = ''

    if (searchParam) {
      searchQueryParam = `&q=${searchParam}`
    }

    const win = this.getWindow()

    if (win) {
      win.open(`${q.translations.menu.supportUrl}?uid=${userId}${searchQueryParam}`, '_blank')
    }
  }

  /**
   * Opens the support chat
   * @returns {boolean} returns false if there was an error initialising the chat
   */
  showSupportChat(): boolean {
    const intercom = this.getIntercom()

    // Messenger is missing when no signed identity was booted. The token request logs that cause once.
    if (typeof intercom !== 'function') {
      return false
    }

    try {
      // NOTE: Pre-populating messages with Intercom('showNewMessage', message) does not work
      // when Intercom bots/workflows are enabled. The bot greeting will override any pre-populated text.
      // We use this to show the home screen so users can see previous conversations.
      intercom('showSpace', 'home')

      return true
    } catch (ex) {
      const error = ex instanceof Error ? ex : new Error('Intercom chat init failed')

      console.warn('Intercom chat init failed', ex)
      this.logService.logError(error)

      return false
    }
  }

  /**
   * Returns the user agent name
   * @returns {string}
   */
  getBrowserName(): string {
    const browser = getBrowserInfo()
    return browser.name?.toLowerCase()
  }

  /**
   * Checks if the support workspace is currently available
   * Has some weaknesses, but does its job
   * @returns {boolean}
   */
  isSupportCurrentlyOnline(): boolean {
    const todayInUserTime = new Date()
    return this.isSupportOnlineAtDate(todayInUserTime)
  }

  /**
   * Returns true, if given date is in our support hours in DE
   * @param {Date} todayInUserTime
   * @returns {boolean}
   */
  isSupportOnlineAtDate(todayInUserTime: Date): boolean {
    const { supportStart, supportEnd } = this.getSupportDateRange(todayInUserTime)

    // support is only weekdays (1 - 5)
    const utcSupportDayOftheWeek = todayInUserTime.getUTCDay()

    if (utcSupportDayOftheWeek > 0 && utcSupportDayOftheWeek < 6) {
      if (isWithinRange(todayInUserTime, supportStart, supportEnd)) {
        return true
      }
    }

    return false
  }

  /**
   * Returns the next date support is online
   * @param {Date} today
   * @returns {Date}
   */
  nextSupportOnlineDate(today: Date): Date {
    const { supportEnd } = this.getSupportDateRange(today)
    let nextDate = today

    if (isWeekend(today) || (isFriday(today) && isAfter(today, supportEnd))) {
      nextDate = addDays(endOfWeek(today, 1), 1)
    } else if (isAfter(today, supportEnd)) {
      nextDate = addDays(today, 1)
    }

    return nextDate
  }

  /**
   * Returns the supportStart and supportEnd dates with the correct timezone
   * @param {Date} todayInUserTime
   * @returns {{ supportStart: Date; supportEnd: Date; }}
   */
  getSupportDateRange(todayInUserTime: Date): { supportStart: Date; supportEnd: Date } {
    // Define the last Sunday in March and the last Sunday in October for the given year
    const getLastSunday = (month: number, year: number) => {
      const lastDay = new Date(Date.UTC(year, month + 1, 0))

      lastDay.setUTCDate(lastDay.getUTCDate() - lastDay.getUTCDay())

      return lastDay
    }

    const year = todayInUserTime.getUTCFullYear()
    const dstStart = getLastSunday(2, year) // March (2 for zero-indexed month)
    const dstEnd = getLastSunday(9, year) // October (9 for zero-indexed month)

    // Check if the current date is within DST
    const isDST = todayInUserTime >= dstStart && todayInUserTime < dstEnd

    // Set the timezone offset based on DST status
    const CESTTimeZoneOffset = isDST ? 2 : 1

    const supportStart = new Date(
      Date.UTC(
        todayInUserTime.getUTCFullYear(),
        todayInUserTime.getUTCMonth(),
        todayInUserTime.getUTCDate(),
        9 - CESTTimeZoneOffset,
        0,
        0
      )
    )

    const supportEnd = new Date(supportStart.getTime() + 8 * 60 * 60 * 1000) // supportEnd is 8 hours after supportStart

    return { supportStart, supportEnd }
  }

  /**
   * Sets the current main scrolling element
   * @param {Element} element
   */
  setScrollingElement(element: Element): void {
    this.scrollingElement = element
    this.setContentResizeObservable()
    this.scrollingElement$.next(element)
  }

  /**
   * Gets the current main scrolling element
   * @returns {Element}
   */
  getScrollingElement(): Element {
    return this.scrollingElement ? this.scrollingElement : this.getDocument().scrollingElement
  }

  setMainViewElementRef(elementRef: ElementRef<HTMLElement>): void {
    this.mainViewElementRef = elementRef
    this.mainViewWidth$.next(elementRef.nativeElement.getBoundingClientRect().width)

    if (isTest()) {
      return
    }

    if (this.mainContentResizeObserver) {
      this.mainContentResizeObserver.disconnect()
    }

    this.mainContentResizeObserver = new ResizeObserver(entries => {
      entries.forEach(entry => {
        this.mainViewWidth$.next(entry.contentRect.width)
      })
    })

    this.mainContentResizeObserver.observe(elementRef.nativeElement)
  }

  getMainViewElementRef(): ElementRef<HTMLElement> {
    return this.mainViewElementRef
  }

  /**
   * Observes the available page width, including its latest measurement.
   * @returns {Observable<number>} Width after a 200 ms debounce.
   */
  selectMainViewWidth(): Observable<number> {
    return this.mainViewWidth$.pipe(distinctUntilChanged(), debounceTime(200))
  }

  /**
   * Sets the menu scrolling element
   * @param {Element} element
   */
  setMenuScrollingElement(element: Element): void {
    this.menuScrollingElement = element
  }

  /**
   * Gets the current list scrolling element (used in split view)
   * @returns {Element}
   */
  getListScrollingElement(): Element {
    return this.listScrollingElement
  }

  /**
   * Sets the list scrolling element
   * @param {Element} element
   */
  setListScrollingElement(element: Element): void {
    this.listScrollingElement = element
    this.setContentResizeObservable()
    this.listScrollingElement$.next(element)
  }

  /**
   * Gets the current detail page scrolling element (used in split view)
   * @returns {Element}
   */
  getDetailScrollingElement(): Element {
    return this.detailScrollingElement
  }

  /**
   * Sets the detail page scrolling element
   * @param {Element} element
   */
  setDetailScrollingElement(element: Element): void {
    this.detailScrollingElement = element
    this.detailScrollingElement$.next(element)
  }

  /**
   * Gets the current popup scrolling element
   * @returns {Element}
   */
  getPopUpScrollingElement(): Element {
    return this.popUpScrollingElement
  }

  /**
   * Sets the popUp scrolling element
   * @param {Element} element
   */
  setPopUpScrollingElement(element: Element): void {
    this.popUpScrollingElement = element
  }

  /**
   * Gets the menu scrolling element
   * @returns {Element}
   */
  getMenuScrollingElement(): Element {
    return this.menuScrollingElement ? this.menuScrollingElement : this.getDocument().scrollingElement
  }

  /**
   * Determines if the browser has dark mode set
   * @returns {boolean}
   */
  isBrowserDarkMode(): boolean {
    try {
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    } catch (_) {
      return false
    }
  }

  get isMobilePhone(): boolean {
    return (
      !!window.navigator.userAgent.match(/Android/) ||
      !!navigator.userAgent.match(/iPhone/) ||
      !!navigator.platform.match(/iPhone/)
    )
  }

  /**
   * Gets the device's OS
   * @returns {string}
   */
  getOS(): string {
    const userAgent = window.navigator.userAgent,
      platform = window.navigator.platform,
      macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
      windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
      iosPlatforms = ['iPhone', 'iPad', 'iPod']

    if (macosPlatforms.indexOf(platform) !== -1) {
      return 'Mac'
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      return 'iOS'
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      return 'Windows'
    } else if (/Android/.test(userAgent)) {
      return 'Android'
    } else if (/Linux/.test(platform)) {
      return 'Linux'
    } else {
      return 'Unknown'
    }
  }

  /**
   * Gets the device's OS version
   * @returns {string}
   */
  getOSVersion(): string {
    const userAgent = window.navigator.userAgent
    const os = this.getOS()

    switch (os) {
      case 'Mac': {
        const match = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/i)

        if (match) {
          return match[1].replace(/_/g, '.')
        }

        break
      }
      case 'Windows': {
        const match = userAgent.match(/Windows NT (\d+\.\d+)/i)

        if (match) {
          const version = match[1]
          const windowsVersions = {
            '10.0': '10',
            '6.3': '8.1',
            '6.2': '8',
            '6.1': '7',
            '6.0': 'Vista',
            '5.1': 'XP',
            '5.0': '2000'
          }

          return windowsVersions[version] || version
        }

        break
      }
      case 'iOS': {
        const match = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/i)

        if (match) {
          return match[1].replace(/_/g, '.')
        }

        break
      }
      case 'Android': {
        const match = userAgent.match(/Android (\d+[.\d]*)/i)

        if (match) {
          return match[1]
        }

        break
      }
      case 'Linux': {
        // Linux version detection is complex and not always reliable from user agent
        return 'Unknown'
      }
      default:
        return 'Unknown'
    }

    return 'Unknown'
  }

  /**
   * Determines if the device is from apple
   * @returns {boolean}
   */
  get isAppleDevice(): boolean {
    return ['Mac', 'iOS'].includes(this.getOS())
  }

  /**
   * Observes the device size and sets the deviceSize signal
   */
  private observeDeviceSize() {
    if (!isResizeObserverAvailable()) {
      return
    }

    this.windowSizeObserver = createResizeObserver((entries: ResizeObserverEntry[]) => {
      entries.forEach(entry => {
        const { width } = entry.contentRect
        // Untracked: Firefox can deliver resize notifications while Angular is rendering a template
        untracked(() => {
          this._deviceSize.set(this.getDeviceCategoryBySize(width))
        })
      })
    })

    this.windowSizeObserver.observe(document.body)
  }

  /**
   * Getter for the deviceSize signal
   * @returns {Signal<WINDOWSIZE>}
   */
  get deviceSize(): Signal<WINDOWSIZE> {
    return this._deviceSize.asReadonly()
  }

  /**
   * Adds the 'grabbing' class to the body element
   */
  addGrabbingClass(): void {
    document.body.classList.add('grabbing')
  }

  /**
   * Removes the 'grabbing' class from the body element
   */
  removeGrabbingClass(): void {
    document.body.classList.remove('grabbing')
  }

  /**
   * Adds the 'ew-resize' cursor class to the body element
   */
  addEwResizeCursorClass(): void {
    document.body.classList.add('dragging-ew')
  }

  /**
   * Removes the 'ew-resize' cursor class from the body element
   */
  removeEwResizeCursorClass(): void {
    document.body.classList.remove('dragging-ew')
  }

  /**
   * Adds a class to disable text selection globally
   */
  addNoSelectClass(): void {
    document.body.classList.add('aw-no-select')
  }

  /**
   * Removes the class that disables text selection globally
   */
  removeNoSelectClass(): void {
    document.body.classList.remove('aw-no-select')
  }

  /**
   * Navigates to an external URL using window.location.replace
   * @param {string} url The URL to navigate to
   */
  navigateToExternalUrl(url: string): void {
    window.location.replace(url)
  }

  /**
   * Returns a signal that becomes true when the user has been continuously active for the specified duration.
   * Activity is tracked from app startup while the page is visible. Timer resets when page becomes hidden.
   * Must be called within an injection context (component construction or field initialization).
   * @param {Duration} duration The minimum duration the user should be active
   * @returns {Signal<boolean>}
   */
  hasBeenActiveForDuration(duration: Duration): Signal<boolean> {
    assertInInjectionContext(this.hasBeenActiveForDuration)

    const durationInMilliseconds = durationToMilliseconds(duration)

    if (durationInMilliseconds <= 0) {
      return signal(true).asReadonly()
    }

    return computed(() => this.getHasBeenActiveForDuration(durationInMilliseconds))
  }

  /**
   * Returns whether the elapsed active time has reached the given duration
   * @param {number} durationInMilliseconds The minimum active duration in milliseconds
   * @returns {boolean}
   * @withSignal {Signal<number | null>} elapsedTimeSignal
   */
  private getHasBeenActiveForDuration(durationInMilliseconds: number): boolean {
    const elapsed = this.elapsedTimeSignal()
    return elapsed !== null && elapsed >= durationInMilliseconds
  }

  /**
   * Returns the elapsed time signal for use in reactive computations.
   * Updates every second while the page is visible, returns null when hidden.
   * @returns {Signal<number | null>} Elapsed time in milliseconds since page became visible, or null
   */
  getElapsedTime(): Signal<number | null> {
    return this.elapsedTimeSignal
  }

  /**
   * Returns whether the browser document is currently visible.
   * @returns {boolean} True when the document is visible
   */
  isDocumentVisible(): boolean {
    return this.document.visibilityState === 'visible'
  }

  /**
   * Creates the observable that tracks elapsed milliseconds while page is visible.
   * Uses a counter that accumulates from 0 each time the page becomes visible.
   * Timer resets to 0 when page becomes visible after being hidden.
   * @returns {Observable<number | null>} Elapsed time in ms, or null when page is hidden
   */
  private createActiveTimeStream(): Observable<number | null> {
    return this.visibilityChange$.pipe(
      startWith(this.isDocumentVisible()),
      switchMap(isVisible => {
        if (!isVisible) {
          return of(null)
        }

        // Create a fresh 1-sec interval that always starts from 0.
        // Each time visibility returns, switchMap unsubscribes from
        // the old interval and creates a new one starting at 0.
        return interval(1000).pipe(
          scan(elapsed => elapsed + 1000, 0),
          startWith(0)
        )
      }),
      // refCount: false keeps the multicast permanently connected. With refCount: true the
      // ReplaySubject disconnects when all subscribers unsubscribe and then synchronously
      // replays the buffered value into the next subscriber. When that subscriber is an
      // Angular reactive context (e.g. a computed() reading elapsedTimeSignal) this write
      // throws NG0600. BrowserService is a root singleton so keeping the stream alive is correct.
      shareReplay({ bufferSize: 1, refCount: false })
    )
  }
}
